import { ImageIcon } from '@saleshandy/design-system';
import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Images } from '../../../../../../../shared/app-constants';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import StepWrapper from '../../../../../shared/stepwrapper';
import { SequenceStepSelectorProps } from '../../../type';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import { stepsSelectionData } from '../util/helper';
import { setAiSeqChatMessage } from '../../../../../sequence-slice';

/**
 * @description
 * Component to select sequence steps for the AI sequence generator modal.
 * It displays a list of steps with descriptions and allows the user to select one.
 *
 * @param handleMoveToNextStep - Function to move to the next step
 * @param handleModalClose - Function to close the modal
 * @param saveStepSelectionDetails - Function to save the step selection details API Call
 * @returns {JSX.Element} - react component
 */

const SequenceStepSelector: React.FC<SequenceStepSelectorProps> = ({
  saveCurrentStepDetails,
}) => {
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
  const dispatch = useDispatch();

  const handleSaveStepSelectionDetails = useCallback(() => {
    dispatch(setAiSeqChatMessage(selectedStepId));
    saveCurrentStepDetails({
      sequenceLength: selectedStepId,
    });
  }, [selectedStepId, saveCurrentStepDetails]);

  return (
    <StepWrapper heading="Choose How Many Emails to Send">
      <div className="sequence-selector">
        <div className="selector-container">
          <div className="steps-list">
            {stepsSelectionData?.map((step) => (
              <div
                key={step?.id}
                className={`step-card ${
                  selectedStepId === step?.id ? 'selected' : ''
                }`}
                role="button"
                tabIndex={0}
                {...accessibleOnClick(() => setSelectedStepId(step?.id))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedStepId(step?.id);
                  }
                }}
              >
                <div className="step-number">
                  {step?.title} <span>Steps</span>
                  {step?.isRecommended && (
                    <div className="recommended-chip">Recommended</div>
                  )}
                </div>
                <div className="step-description">{step?.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AiSequenceModalFooter
        disableContinueBtn={!selectedStepId}
        onClickContinue={handleSaveStepSelectionDetails}
      />
    </StepWrapper>
  );
};

export default memo(SequenceStepSelector);
